import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsApi, Product } from '../../lib/api/products';
import { Plus, Package, Globe, Users } from 'lucide-react';

const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount / 100);
};

export const ProductsPage: React.FC = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'active' | 'draft'>('all');

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            setLoading(true);
            const data = await productsApi.list();
            setProducts(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Filter products
    const filteredProducts = products.filter(p => {
        if (filter === 'active') return p.isActive;
        if (filter === 'draft') return !p.isActive;
        return true;
    });

    // Get primary price for a product
    const getPrimaryPrice = (product: Product) => {
        const variant = product.variants?.[0];
        const price = variant?.prices?.[0];
        if (!price) return null;
        return { amount: price.amount, currency: variant.currency, interval: price.interval };
    };

    return (
        <div className="h-full flex flex-col p-1 animate-fade-in max-w-6xl mx-auto w-full">
            <div className="flex-1 bg-white border border-neutral-200 rounded-lg overflow-hidden flex flex-col shadow-sm">

                {/* Toolbar */}
                <div className="flex items-center justify-between p-2 border-b border-neutral-200 bg-neutral-50/50">
                    <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wider px-2">Products</span>
                    <div className="flex items-center gap-2">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value as any)}
                            className="text-xs border border-neutral-200 rounded-md bg-white text-neutral-600 py-1 pl-2 pr-6 focus:ring-0 focus:border-neutral-300"
                        >
                            <option value="all">All Products</option>
                            <option value="active">Active</option>
                            <option value="draft">Draft</option>
                        </select>
                        <button
                            onClick={() => navigate('/payments/products/new')}
                            className="btn-primary !px-4 py-1.5 flex items-center gap-2 shadow-sm shadow-accent-primary/20"
                        >
                            <Plus size={14} />
                            New Product
                        </button>
                    </div>
                </div>

                {/* Header Grid */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-neutral-200 bg-neutral-50 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                    <div className="col-span-4">Product</div>
                    <div className="col-span-2">Price</div>
                    <div className="col-span-2">Regions</div>
                    <div className="col-span-2">Subscribers</div>
                    <div className="col-span-2 text-right">Status</div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <p className="text-sm">Loading products...</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="p-12 flex flex-col items-center justify-center text-center text-neutral-500 h-full">
                            <Package size={32} className="mb-3 text-neutral-300" />
                            <p className="text-sm">No products found</p>
                            <button
                                onClick={() => navigate('/payments/products/new')}
                                className="mt-4 text-xs text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Create your first product
                            </button>
                        </div>
                    ) : (
                        <div className="divide-y divide-neutral-100">
                            {filteredProducts.map(product => {
                                const primaryPrice = getPrimaryPrice(product);
                                const subscriberCount = product.subscriberCount || product._count?.subscriptions || 0;
                                const regionCount = product.variants?.length || 0;

                                return (
                                    <div
                                        key={product.id}
                                        onClick={() => navigate(`/payments/products/${product.id}`)}
                                        className="grid grid-cols-12 gap-4 px-4 py-2 items-center hover:bg-neutral-50/80 transition-colors text-sm group cursor-pointer"
                                    >
                                        <div className="col-span-4 min-w-0 flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
                                                <Package size={16} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-neutral-900 truncate">{product.name}</div>
                                                <div className="text-xs text-neutral-400 font-mono truncate">{product.id}</div>
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            {primaryPrice ? (
                                                <span className="font-medium text-neutral-900">
                                                    {formatCurrency(primaryPrice.amount, primaryPrice.currency)}
                                                    <span className="text-neutral-400 font-normal">/{primaryPrice.interval}</span>
                                                </span>
                                            ) : (
                                                <span className="text-neutral-400 text-xs">No price</span>
                                            )}
                                        </div>
                                        <div className="col-span-2">
                                            <span className="inline-flex items-center gap-1 text-neutral-600">
                                                <Globe size={12} className="text-neutral-400" />
                                                {regionCount} region{regionCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                        <div className="col-span-2">
                                            <span className="inline-flex items-center gap-1 text-neutral-600">
                                                <Users size={12} className="text-neutral-400" />
                                                {subscriberCount}
                                            </span>
                                        </div>
                                        <div className="col-span-2 text-right" onClick={e => e.stopPropagation()}>
                                            <label className="relative inline-flex items-center cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="sr-only peer"
                                                    checked={product.isActive}
                                                    onChange={async (e) => {
                                                        e.stopPropagation();
                                                        try {
                                                            const newValue = e.target.checked;
                                                            // Optimistic update
                                                            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, isActive: newValue } : p));
                                                            await productsApi.update(product.id, { isActive: newValue });
                                                        } catch (err) {
                                                            console.error('Failed to update status', err);
                                                            // Revert
                                                            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, isActive: !product.isActive } : p));
                                                        }
                                                    }}
                                                />
                                                <div className="w-9 h-5 bg-neutral-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                                                <span className="ml-2 text-xs font-medium text-neutral-600">{product.isActive ? 'Active' : 'Draft'}</span>
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
